import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { sortingToOrdering, orderingToSorting } from '../common/TablePanel/tableUtils';

/**
 * Syncs MRT sorting state with the `ordering` URL search param so that
 * sort order is preserved when sharing links.
 *
 * @param {Array} defaultSorting - Fallback when URL has no `ordering`, e.g. [{ id: 'datetime', desc: true }]
 * @returns {[Array, Function]} - [sorting, setSorting] compatible with useReusableTable
 */
export const useUrlSorting = (defaultSorting = []) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const [sorting, setSortingState] = useState(() => {
    const fromUrl = orderingToSorting(searchParams.get('ordering'));
    return fromUrl && fromUrl.length > 0 ? fromUrl : defaultSorting;
  });

  useEffect(() => {
    const fromUrl = orderingToSorting(searchParams.get('ordering'));
    if (fromUrl && fromUrl.length > 0) {
      setSortingState(prev => {
        const prevStr = sortingToOrdering(prev);
        const urlStr = sortingToOrdering(fromUrl);
        return prevStr === urlStr ? prev : fromUrl;
      });
    }
  }, [searchParams]);

  const setSorting = useCallback((updater) => {
    const newSorting = typeof updater === 'function' ? updater(sorting) : updater;
    setSortingState(newSorting);

    const nextParams = new URLSearchParams(searchParams);
    const ordering = sortingToOrdering(newSorting);
    if (ordering) {
      nextParams.set('ordering', ordering);
    } else {
      nextParams.delete('ordering');
    }
    setSearchParams(nextParams, { replace: true });
  }, [sorting, searchParams, setSearchParams]);

  return [sorting, setSorting];
};
